import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  InputAdornment,
  Alert,
  CircularProgress,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { orderApi } from '../services/api';
import { Order, OrderStatus, ApiResponse, PaginationInfo, OrderPaginationParams } from '../types';
import { ORDER_STATUS, APP_CONFIG } from '../utils/constants';
import Pagination from '../components/Pagination';

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 10,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [paginationParams, setPaginationParams] = useState<OrderPaginationParams>({
    page: 1,
    limit: 10,
    search: '',
    status: '',
    sort_by: 'created_at',
    sort_order: 'DESC',
  });
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async (params?: OrderPaginationParams) => {
    try {
      setLoading(true);
      setError(null);
      const response = await orderApi.getAll(params || paginationParams);
      if (response.status === 'success' && response.data) {
        setOrders(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError(response.message || 'Failed to load orders');
      }
    } catch (err) {
      setError('Error loading orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  // Pagination handlers
  const handlePageChange = (page: number) => {
    const newParams = { ...paginationParams, page };
    setPaginationParams(newParams);
    loadOrders(newParams);
  };

  const handlePageSizeChange = (pageSize: number) => {
    const newParams = { ...paginationParams, page: 1, limit: pageSize };
    setPaginationParams(newParams);
    loadOrders(newParams);
  };

  const handleSortChange = (sortBy: string, sortOrder: 'ASC' | 'DESC') => {
    const newParams = { ...paginationParams, sort_by: sortBy, sort_order: sortOrder, page: 1 };
    setPaginationParams(newParams);
    loadOrders(newParams);
  };

  const handleSearch = () => {
    const newParams = {
      ...paginationParams,
      search: searchTerm,
      status: statusFilter === 'all' ? '' : statusFilter,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      page: 1,
    };
    setPaginationParams(newParams);
    loadOrders(newParams);
  };

  const handleStatusFilterChange = (status: string) => {
    const newParams = {
      ...paginationParams,
      status: status === 'all' ? '' : status,
      page: 1,
    };
    setPaginationParams(newParams);
    setStatusFilter(status);
    loadOrders(newParams);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    const newParams = {
      ...paginationParams,
      search: '',
      status: '',
      date_from: undefined,
      date_to: undefined,
      page: 1,
    };
    setPaginationParams(newParams);
    loadOrders(newParams);
  };



  const getStatusColor = (status: string) => {
    if (!status) return 'default';
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'warning'; // Yellow
      case 'delivered': return 'success'; // Green
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    if (!status) return 'UNKNOWN';
    return status.replace('_', ' ').toUpperCase();
  };

  const getCustomerName = (order: any) => {
    // Use customer_name from the order data if available
    if (order.customer_name) {
      return order.customer_name;
    }
    // Fallback to customer ID if customer name is not available
    return `Customer #${order.customer_id}`;
  };

  // Orders are now paginated on the server side, so we use the orders directly


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <TextField
              fullWidth
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          
          <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e: SelectChangeEvent) => handleStatusFilterChange(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                {Object.values(ORDER_STATUS).map(status => (
                  <MenuItem key={status} value={status}>
                    {getStatusLabel(status)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          
          <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <TextField
              fullWidth
              type="date"
              label="From Date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          
          <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
            <TextField
              fullWidth
              type="date"
              label="To Date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          
          <Box sx={{ flex: '0 1 120px' }}>
            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              fullWidth
              sx={{ mb: 1 }}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<RefreshIcon />}
              fullWidth
            >
              Clear
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Orders Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Delivery Date</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Total Amount</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow 
                  key={order.id} 
                  hover 
                  onClick={() => navigate(`/orders/${order.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {order.order_number || `#${order.id}`}
                    </Typography>
                  </TableCell>
                  <TableCell>{getCustomerName(order)}</TableCell>
                  <TableCell>
                    {new Date(order.order_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {order.delivery_date 
                      ? new Date(order.delivery_date).toLocaleDateString()
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.items?.length || 0} items
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {APP_CONFIG.CURRENCY}{order.total_amount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(order.status)}
                      color={getStatusColor(order.status) as any}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </Paper>


    </Box>
  );
};

export default Orders; 